export interface UptimeRobotResponse {
  stat: string;
  monitors?: UptimeRobotMonitor[];
  error?: {
    message: string;
    type: string;
  };
}

export interface UptimeRobotMonitor {
  id: number;
  friendly_name: string;
  status: number;
  custom_uptime_ratio?: string;
  response_times?: { value: number }[];
}

export interface ServiceStatus {
  id: string;
  name: string;
  status: 'operational' | 'degraded' | 'outage' | 'development' | 'paused' | 'unknown';
  uptime: { day: number; week: number; month: number } | null;
  responseTimeMs: number | null;
}

export interface StatusData {
  overall: 'operational' | 'degraded' | 'outage' | 'unknown';
  updatedAt: string;
  stale: boolean;
  services: ServiceStatus[];
}

export class RateLimitError extends Error {
  retryAfter: number;
  constructor(retryAfter: number) {
    super('Rate limit exceeded');
    this.retryAfter = retryAfter;
    this.name = 'RateLimitError';
  }
}

function parseStatus(code: number): ServiceStatus['status'] {
  switch (code) {
    case 0: return 'paused';
    case 1: return 'unknown';
    case 2: return 'operational';
    case 8: return 'degraded';
    case 9: return 'outage';
    default: return 'unknown';
  }
}

async function fetchMonitor(apiKey: string, monitorId: string): Promise<ServiceStatus | null> {
  if (!apiKey) return null;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

  try {
    const body = new URLSearchParams({
      api_key: apiKey,
      format: 'json',
      monitors: monitorId,
      custom_uptime_ratios: '1-7-30',
      response_times: '1',
    });

    const res = await fetch('https://api.uptimerobot.com/v2/getMonitors', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
      signal: controller.signal,
    });

    if (res.status === 429) {
      const retryAfterStr = res.headers.get('Retry-After');
      const retryAfter = retryAfterStr ? parseInt(retryAfterStr, 10) : 60;
      throw new RateLimitError(retryAfter);
    }
    if (!res.ok) {
      throw new Error(`HTTP Error: ${res.status}`);
    }

    const data: UptimeRobotResponse = await res.json();
    if (data.stat !== 'ok' || !data.monitors || data.monitors.length === 0) {
      throw new Error(data.error?.message || 'Invalid response from UptimeRobot');
    }

    const m = data.monitors[0];
    if (!m) {
      throw new Error('Monitor not found in response');
    }
    const statusStr = parseStatus(m.status);

    let uptime = null;
    if (m.custom_uptime_ratio) {
      const ratios = m.custom_uptime_ratio.split('-');
      if (ratios.length >= 3) {
        uptime = {
          day: parseFloat(ratios[0] || '0'),
          week: parseFloat(ratios[1] || '0'),
          month: parseFloat(ratios[2] || '0'),
        };
      }
    }

    let responseTimeMs = null;
    if (m.response_times && m.response_times.length > 0) {
      const firstResponse = m.response_times[0];
      if (firstResponse) {
        responseTimeMs = parseInt(firstResponse.value.toString(), 10);
      }
    }

    return {
      id: String(m.id),
      name: m.friendly_name || `Servizio ${m.id}`,
      status: statusStr,
      uptime,
      responseTimeMs: !isNaN(responseTimeMs as number) ? responseTimeMs : null,
    };
  } catch (err: any) {
    if (err.name === 'RateLimitError') {
      throw err;
    }
    if (err.name === 'AbortError') {
      console.warn(`Timeout per monitor ${monitorId}`);
    } else if (err.message && err.message.includes('Failed to fetch')) {
      console.warn(`CORS o Network error per monitor ${monitorId}`, err);
    } else {
      console.error(`Errore fetch monitor ${monitorId}:`, err);
    }
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function fetchAllStatuses(): Promise<StatusData> {
  const siteKey = import.meta.env.VITE_UPTIMEROBOT_SITE_KEY;
  const javaKey = import.meta.env.VITE_UPTIMEROBOT_JAVA_KEY;

  let siteStatus: ServiceStatus | null = null;
  let javaStatus: ServiceStatus | null = null;

  try {
    const results = await Promise.all([
      fetchMonitor(siteKey, '803626911'),
      fetchMonitor(javaKey, '803626903'),
    ]);
    siteStatus = results[0];
    javaStatus = results[1];
  } catch (err: any) {
    if (err.name === 'RateLimitError') {
      throw err;
    }
  }

  const services: ServiceStatus[] = [];

  if (siteStatus) services.push({ ...siteStatus, name: 'Sito web' });
  else {
    services.push({
      id: '803626911',
      name: 'Sito web',
      status: 'unknown',
      uptime: null,
      responseTimeMs: null,
    });
  }

  if (javaStatus) services.push({ ...javaStatus, name: 'Java Server' });
  else {
    services.push({
      id: '803626903',
      name: 'Java Server',
      status: 'unknown',
      uptime: null,
      responseTimeMs: null,
    });
  }

  // Bedrock statico
  services.push({
    id: '801301550',
    name: 'Bedrock Server',
    status: 'development',
    uptime: null,
    responseTimeMs: null,
  });

  let hasDegraded = false;
  let hasOutage = false;
  let validCount = 0;

  for (const s of services) {
    if (s.status === 'degraded') hasDegraded = true;
    if (s.status === 'outage') hasOutage = true;
    if (s.status === 'operational') validCount++;
  }

  let overall: StatusData['overall'] = 'operational';
  if (hasOutage) overall = 'outage';
  else if (hasDegraded) overall = 'degraded';
  
  // Se l'unico stato conosciuto è Bedrock (che è development) o non c'è nulla di valido
  if (validCount === 0 && !hasOutage && !hasDegraded) {
    overall = 'unknown';
  }

  return {
    overall,
    updatedAt: new Date().toISOString(),
    stale: false,
    services,
  };
}
